import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveFilled, UnarchiveLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { SocialBenefit, SocialBenefitToCreate } from '@services/member/list/social-benefits/social-benefits.types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useSocialBenefitsPageVM } from './useSocialBenefitsPageVM';
import { Formik } from 'formik';
import { formatMoney } from '@utils/numbers';

export default function SocialBenefitsPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchiveSocialBenefits,
        needReload,
        setIsArchived,
        updateSocialBenefit,
        createSocialBenefit,
        MySwal
    } = useSocialBenefitsPageVM();
    const columnHelper = createColumnHelper<SocialBenefit>();
    const navigate = useNavigate();

    const handleModificationSocialBenefits = async (socialBenefit: SocialBenefitToCreate | null, id?: number) => {
        await MySwal.fire<SocialBenefitToCreate>({
            title: socialBenefit ? "Modification d'un type de prestation sociale" : "Création d'un type de prestation sociale",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: socialBenefit ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{
                        code: socialBenefit?.code || '',
                        label: socialBenefit?.label || '',
                        amount: socialBenefit?.amount || 0,
                        observation: socialBenefit?.observation || '',
                        accountNumber: socialBenefit?.accountNumber || ''
                    }}
                    onSubmit={(values) => {
                        if (id) {
                            updateSocialBenefit({
                                id: id,
                                code: values.code,
                                label: values.label,
                                amount: values.amount,
                                observation: values.observation,
                                accountNumber: values.accountNumber,
                                isArchived: false
                            });
                        } else {
                            createSocialBenefit({
                                code: values.code,
                                label: values.label,
                                amount: values.amount,
                                observation: values.observation,
                                accountNumber: values.accountNumber,
                                isArchived: false
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-social-benefit" onSubmit={handleSubmit}>
                            <Input name="code" required label="Code" placeholder="Code" value={values.code} onChange={handleChange} />
                            <Input name="label" required label="Libellé" placeholder="Libellé" value={values.label} onChange={handleChange} />
                            <Input
                                type="number"
                                rightContent={'€'}
                                name="amount"
                                label="Montant"
                                required
                                placeholder="Montant"
                                value={values.amount}
                                onChange={handleChange}
                            />
                            <Input
                                name="observation"
                                label="Observation"
                                placeholder="Observation"
                                value={values.observation}
                                onChange={handleChange}
                            />
                            <Input
                                name="accountNumber"
                                label="Numéro de compte"
                                required
                                placeholder="Numéro de compte"
                                value={values.accountNumber}
                                onChange={handleChange}
                            />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-social-benefit') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('amount', {
            header: 'Montant',
            enableSorting: true,
            cell: (info) => {
                return <>{formatMoney(info.getValue())}</>;
            }
        }),
        columnHelper.accessor('observation', {
            header: 'Observation',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('accountNumber', {
            header: 'Numéro de compte',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationSocialBenefits(
                                    {
                                        code: info.row.original.code,
                                        label: info.row.original.label,
                                        amount: info.row.original.amount,
                                        observation: info.row.original.observation,
                                        accountNumber: info.row.original.accountNumber,
                                        isArchived: false
                                    },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveSocialBenefits([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<SocialBenefit>[];

    return (
        <>
            <div className="settings-tabs-content social-benefits">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/social-benefits/${row.id}`)}
                    defaultSorting={{
                        field: 'code',
                        direction: 'desc'
                    }}
                    needReload={needReload}
                    createButtonOnClick={() => handleModificationSocialBenefits(null)}
                    createButtonLabel="Créer un type de prestation sociale"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    onMetadata={handleMetadata}
                    deleting={{
                        enabled: isArchived,
                        entity: 'social_benefit_type',
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: SocialBenefit[]) => handleArchiveSocialBenefits(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
